import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Papa from 'papaparse';
import { Observable } from 'rxjs';
import { utils, writeFile } from 'xlsx';
import { ColorsService } from './colors.service';
import { ProjectService } from './project.service';
import { MultipleUsers } from 'src/app/account/account-subscription/assign-slot-popup/assign-slot-popup.component';
import { Load, WorkSpaceStop } from '../types';

@Injectable({
  providedIn: 'root',
})
export class ImportFromCsvService {
  public unit: string;

  constructor(
    private colorService: ColorsService,
    private projectService: ProjectService,
    private translate: TranslateService,
  ) {
    this.projectService.project.subscribe((project) => (this.unit = project.options?.unit));
  }

  public parse(file, type?: 'loadingSpaces' | 'loads' | 'users') {
    return new Observable((observable) => {
      Papa.parse(file, {
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            let data;
            if (type === 'loadingSpaces') {
              data = this.parseLoadingSpacesObjects(results.data);
            } else if (type === 'loads') {
              data = this.parseLoadsObjects(results.data);
            } else if (type === 'users') {
              data = this.parseUsersObjects(results.data);
            } else {
              data = this.parseObjects(results.data);
            }
            observable.next(data);
            observable.complete();
          } catch (error) {
            observable.error(error);
          }
        },
      });
    });
  }

  public downloadCsvTemplate(type: 'loadingSpaces' | 'loads' | 'users') {
    let blob;
    if (type === 'loadingSpaces') {
      blob = new Blob(['\uFEFF', this.createCsvTemplate(type)], {
        type: 'text/csv;charset=utf-8',
      });
    } else if (type === 'loads') {
      blob = new Blob(['\uFEFF', this.createCsvTemplate(type)], {
        type: 'text/csv;charset=utf-8',
      });
    } else if (type === 'users') {
      blob = new Blob(['\uFEFF', this.createCsvTemplate(type)], {
        type: 'text/csv;charset=utf-8',
      });
    }
    const url = window.URL.createObjectURL(blob);
    const fileName = `${this.translate.instant(type) as string}.csv`;
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, fileName);
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    window.URL.revokeObjectURL(url);
  }

  public downloadXlsxTemplate(type: 'loadingSpaces' | 'loads') {
    let data;
    if (type === 'loadingSpaces') {
      data = this.getLoadingSpaceArray();
    } else if (type === 'loads') {
      data = this.getLoadsArray();
    }
    const workbook = utils.book_new();
    const worksheet = utils.aoa_to_sheet(data);
    workbook.SheetNames.push('First');
    workbook.Sheets['First'] = worksheet;
    writeFile(workbook, `${this.translate.instant(type) as string}.xlsx`);
  }

  private createCsvTemplate(type: 'loadingSpaces' | 'loads' | 'users') {
    if (type === 'loadingSpaces') {
      return Papa.unparse(this.getLoadingSpaceArray());
    } else if (type === 'loads') {
      return Papa.unparse(this.getLoadsArray());
    } else if (type === 'users') {
      return Papa.unparse(this.getUsersArray());
    }
  }

  public getLoadingSpaceArray() {
    return [
      [
        [this.translate.instant('loading_type')],
        [this.translate.instant('name')],
        [this.translate.instant('length')],
        [this.translate.instant('width')],
        [this.translate.instant('height')],
        [this.translate.instant('limit')],
        [this.translate.instant('set')],
        [`${this.translate.instant('length') as string} 2`],
        [`${this.translate.instant('width') as string} 2`],
        [`${this.translate.instant('height') as string} 2`],
        [`${this.translate.instant('limit') as string} 2`],
      ],
      [
        1,
        'Set',
        this.unit === 'cm' ? '765' : '298.4',
        this.unit === 'cm' ? '240' : '93.6',
        this.unit === 'cm' ? '270' : '105.3',
        '6000',
        'Y',
        this.unit === 'cm' ? '765' : '298.4',
        this.unit === 'cm' ? '240' : '93.6',
        this.unit === 'cm' ? '270' : '105.3',
        '6000',
      ],
    ];
  }

  public getLoadsArray() {
    return [
      [
        [this.translate.instant('name')],
        [this.translate.instant('length')],
        [this.translate.instant('width')],
        [this.translate.instant('height')],
        [this.translate.instant('weight')],
      ],
      [
        'Euro',
        this.unit === 'cm' ? '120' : '46.8',
        this.unit === 'cm' ? '80' : '31.2',
        this.unit === 'cm' ? '100' : '39',
        '95',
      ],
    ];
  }

  public getUsersArray() {
    return [
      [
        [this.translate.instant('first_name')],
        [this.translate.instant('last_name')],
        [this.translate.instant('email')],
      ],
      ['John', 'Doe', 'john@goodloading.com'],
    ];
  }

  public parseObjects(data) {
    const objectKeys = [
      'type',
      'name',
      'limit',
      'name',
      'length',
      'width',
      'height',
      'quantity',
      'weight',
      'stacking',
      'maxLoadsOnTop',
      'maxWeightOnTop',
      'rotateOnBase',
      'rotateFreely',
      'milkRunLoad',
      'milkRunUnload',
    ];
    const loadingObjects: any[] = [];
    const loadsObjects: any[] = [];
    let type: number;
    this.removeHeadersFromArray(data);
    try {
      const stops: WorkSpaceStop[] = [];
      let origin: WorkSpaceStop;
      let destination: WorkSpaceStop;
      data.forEach((csvData, row) => {
        const loadGroup: any = {};
        const object: any = {};
        csvData.forEach((value, i) => {
          if (i === 0) {
            if (value && Number.isInteger(value)) {
              object[objectKeys[i]] = this.parseNumberValue(value, this.errorName(i, row));
            }
          } else if (i === 1 && object[objectKeys[0]]) {
            !value
              ? (object[objectKeys[i]] = 'truck_without_name')
              : (object[objectKeys[i]] = value.toString());
            this.validateName(object[objectKeys[i]], this.errorName(i, row));
          } else if (i === 2) {
            if (object[objectKeys[0]]) {
              object[objectKeys[i]] = value
                ? this.parseNumberValue(value, this.errorName(i, row))
                : 0;
            }
          } else if (i === 3) {
            if (value && object[objectKeys[0]]) {
              this.dataError(this.errorName(i, row));
            } else if (!object[objectKeys[0]]) {
              !value
                ? (object[objectKeys[i]] = 'load_without_name')
                : (object[objectKeys[i]] = value.toString());
              this.validateName(object[objectKeys[i]], this.errorName(i, row));
            }
          } else if (i >= 4 && i <= 6) {
            if (!object[objectKeys[0]]) {
              object[objectKeys[i]] = this.parseNumberValue(value, this.errorName(i, row));
            } else if (value) {
              const newValue =
                typeof value !== 'number' ? parseFloat(value.replace(/,/g, '.')) : value;
              if (!isNaN(newValue)) {
                object[objectKeys[i]] = value;
              } else {
                this.isNotNumber(this.errorName(i, row));
              }
            }
          } else if (i === 7) {
            if (value && object[objectKeys[0]]) {
              this.dataError(this.errorName(i, row));
            } else if (!object[objectKeys[0]]) {
              object[objectKeys[i]] = this.parseNumberValue(value, this.errorName(i, row));
            }
          } else if (i === 8) {
            if (value && object[objectKeys[0]]) {
              this.dataError(this.errorName(i, row));
            }
            if (value) {
              object[objectKeys[i]] = this.parseNumberValue(value, this.errorName(i, row));
            } else {
              object[objectKeys[i]] = 0;
            }
          } else if (i === 9) {
            if (value && object[objectKeys[0]]) {
              this.dataError(this.errorName(i, row));
            } else if (!object[objectKeys[0]]) {
              object[objectKeys[i]] = this.parseBooleanValue(value, true, this.errorName(i, row));
            }
          } else if (i === 10) {
            if (object[objectKeys[9]] && value) {
              if (object[objectKeys[0]]) {
                this.dataError(this.errorName(i, row));
              }
              if (!Number.isInteger(value)) {
                this.isNotNumber(this.errorName(i, row));
              }
              object['stackingOptions'] = {
                [objectKeys[i]]: this.parseNumberValue(value, this.errorName(i, row)),
              };
            }
          } else if (i === 11) {
            if (object[objectKeys[9]] && value) {
              if (object[objectKeys[0]]) {
                this.dataError(this.errorName(i, row));
              }
              if (!Number.isInteger(value)) {
                this.isNotNumber(this.errorName(i, row));
              }
              object['stackingOptions'] = {
                ...object['stackingOptions'],
                [objectKeys[i]]: this.parseNumberValue(value, this.errorName(i, row)),
              };
            }
          } else if (i === 12) {
            if (value && object[objectKeys[0]]) {
              this.dataError(this.errorName(i, row));
            } else if (!object[objectKeys[0]]) {
              if (!value) {
                object[objectKeys[i]] = true;
              } else {
                object[objectKeys[i]] = this.parseBooleanValue(
                  value,
                  false,
                  this.errorName(i, row),
                );
              }
            }
          } else if (i === 13) {
            if (value && object[objectKeys[0]]) {
              this.dataError(this.errorName(i, row));
            } else if (!object[objectKeys[0]]) {
              if (!value) {
                object[objectKeys[i]] = true;
              } else {
                object[objectKeys[i]] = this.parseBooleanValue(
                  value,
                  false,
                  this.errorName(i, row),
                );
              }
            }
          } else if (i === 14) {
            if (value && object[objectKeys[0]]) {
              this.dataError(this.errorName(i, row));
            } else if (!object[objectKeys[0]] && value) {
              if (!stops.find((stop) => stop.name === value)) {
                stops.push({ name: value });
              }
              origin = { name: value };
            }
          } else if (i === 15) {
            if (value && object[objectKeys[0]]) {
              this.dataError(this.errorName(i, row));
            } else if (!object[objectKeys[0]] && value) {
              if (!stops.find((stop) => stop.name === value)) {
                stops.push({ name: value });
              }
              destination = { name: value };
            }
          }
        });

        if (object.type && object.length && object.width && object.height) {
          object.weight = undefined;
          object.set = false;
          object.limit = object.limit ? object.limit : 0;
          object.length1 = object.set ? object.secondPart.length1 : undefined;
          object.width1 = object.set ? object.secondPart.width1 : undefined;
          object.height1 = object.set ? object.secondPart.height1 : undefined;
          object.limit1 = object.set && object.secondPart.limit1 ? object.secondPart.limit1 : 0;
          object.allowToRotate = undefined;
          object.stops = stops;
          loadingObjects.push(object);
        } else if (object.type) {
          type = object.type;
        } else {
          object.color = this.colorService.getColor();
          if (origin && destination) {
            object.loadingPlaces = {
              origin,
              destination,
            };
          }
          loadGroup.load = object;
          loadGroup.quantity = object.quantity;
          loadsObjects.push(loadGroup);
        }
      });
      return { loadingObjects, loadData: { loadsObjects, type }, stops };
    } catch (error) {
      this.errorHandle(error);
    }
  }

  public parseLoadingSpacesObjects(data) {
    const objectKeys = [
      'type',
      'name',
      'length',
      'width',
      'height',
      'limit',
      'set',
      'length1',
      'width1',
      'height1',
      'limit1',
    ];
    const loadingSpacesObjects: { loadingObjects; loadData: { loadsObjects; type } }[] = [];
    this.removeHeadersFromArray(data);
    try {
      data.forEach((loadData, row) => {
        const loadingSpace: any = {};
        loadData.forEach((value, i) => {
          if (i === 0) {
            const parsedValue = this.parseNumberValue(value, this.errorName(i, row));
            if (Number.isInteger(parsedValue) && parsedValue >= 1 && parsedValue <= 3) {
              loadingSpace[objectKeys[i]] = parsedValue;
            } else {
              this.isNotNumber(this.errorName(i, row));
            }
          } else if (i === 1) {
            !value
              ? (loadingSpace[objectKeys[i]] = 'truck_without_name')
              : (loadingSpace[objectKeys[i]] = value.toString());
            this.validateName(loadingSpace[objectKeys[i]], this.errorName(i, row));
          } else if (i >= 2 && i <= 5) {
            loadingSpace[objectKeys[i]] = this.parseNumberValue(value, this.errorName(i, row));
          } else if (i === 6) {
            loadingSpace[objectKeys[i]] = this.parseBooleanValue(
              value,
              true,
              this.errorName(i, row),
            );
          } else if (loadingSpace[objectKeys[6]] && i >= 7 && i <= 10) {
            loadingSpace[objectKeys[i]] = this.parseNumberValue(value, this.errorName(i, row));
          }
        });
        loadingSpacesObjects.push(loadingSpace);
      });
      return loadingSpacesObjects;
    } catch (error) {
      this.errorHandle(error);
    }
  }

  public parseLoadsObjects(data) {
    const objectKeys = ['name', 'length', 'width', 'height', 'weight'];
    const loadsObjects: Load[] = [];
    this.removeHeadersFromArray(data);
    try {
      data.forEach((loadData, row) => {
        const load: any = {};
        loadData.forEach((value, i) => {
          if (i === 0) {
            !value
              ? (load[objectKeys[i]] = 'load_without_name')
              : (load[objectKeys[i]] = value.toString());
            this.validateName(load[objectKeys[i]], this.errorName(i, row));
          } else if (i >= 1 && i <= 3) {
            load[objectKeys[i]] = this.parseNumberValue(value, this.errorName(i, row));
          } else if (i === 4) {
            if (value) {
              load[objectKeys[i]] = this.parseNumberValue(value, this.errorName(i, row));
            } else {
              load[objectKeys[i]] = 0;
            }
          }
        });
        load.stacking = false;
        loadsObjects.push(load);
      });
      return loadsObjects;
    } catch (error) {
      this.errorHandle(error);
    }
  }

  public parseUsersObjects(data): MultipleUsers[] {
    const objectKeys: string[] = ['firstName', 'lastName', 'newUserEmail'];
    const usersObjects: MultipleUsers[] = [];
    this.removeHeadersFromArray(data);
    try {
      data.forEach((users, row): void => {
        const user: any = {};
        users.forEach((value, i): void => {
          if (i === 0) {
            if (value) {
              user[objectKeys[i]] = value.toString();
              this.validateName(user[objectKeys[i]], this.errorName(i, row));
            }
          } else if (i === 1) {
            if (value) {
              user[objectKeys[i]] = value.toString();
              this.validateName(user[objectKeys[i]], this.errorName(i, row));
            }
          } else if (i === 2) {
            user[objectKeys[i]] = this.validateEmail(value, this.errorName(i, row));
            if (!user[objectKeys[i]]) {
              user[objectKeys[i]] = value.toString();
            }
          }
        });
        usersObjects.push(user);
      });
      return usersObjects;
    } catch (error) {
      this.errorHandle(error);
    }
  }

  private removeHeadersFromArray(array) {
    array.splice(0, 1);
  }

  private parseNumberValue(value: number | string, errorName: string) {
    value = typeof value !== 'number' ? parseFloat(value.replace(/,/g, '.')) : value;
    if (!isNaN(value) && value > 0) {
      return value;
    } else {
      const e = new Error('error_not_number');
      e.name = errorName;
      throw e;
    }
  }

  private validateName(name, errorName: string) {
    if (name.length > 255) {
      const e = new Error('error_too_long');
      e.name = errorName;
      throw e;
    }
  }

  private validateEmail(email: string, errorName: string) {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const test = re.test(String(email).toLowerCase());
    if (test) {
      return email;
    } else {
      const e = new Error('error_email');
      e.name = errorName;
      throw e;
    }
  }

  private parseBooleanValue(value, stackable: boolean, errorName: string) {
    if (typeof value === 'string' && value.toLowerCase() === 'y') {
      return true;
    } else if (typeof value === 'string' && value.toLowerCase() === 'n') {
      return false;
    } else if (stackable) {
      const e = new Error('error_loads_stackable');
      e.name = errorName;
      throw e;
    } else if (!stackable) {
      const e = new Error('error_loads_rotation');
      e.name = errorName;
      throw e;
    }
  }

  // private isRequired(errorName: string) {
  //   const e = new Error('error_required');
  //   e.name = errorName;
  //   throw e;
  // }

  private dataError(errorName: string) {
    const e = new Error('error_wrong_csv_data');
    e.name = errorName;
    throw e;
  }

  private isNotNumber(errorName: string) {
    const e = new Error('error_not_number');
    e.name = errorName;
    throw e;
  }

  private errorName(column: number, row: number) {
    return `${this.translate.instant('field') as string} ${String.fromCharCode(65 + column)}${
      row + 2
    }`;
  }

  private errorHandle(err) {
    throw err;
  }
}

declare global {
  interface Navigator {
    msSaveBlob: (blob: any, defaultName?: string) => boolean;
  }
}
